import React, { useState, useContext, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { TbTruckReturn } from 'react-icons/tb';
import {
  MdDashboard,
  MdShoppingCart,
  MdInventory2,
  MdLocalShipping,
  MdPerson,
  MdLock,
  MdLogout,
  MdMenu,
  MdClose,
  MdVerified,
  MdPending
} from 'react-icons/md';
import AuthUser from '../../context/AuthUser';
import Cookies from 'js-cookie';
import RegistrationStatus from '../../context/RegistrationStatus';
import ProfileNameContext from '../../context/ProfileName';
import axios from 'axios';
import { decrpt } from '../../component/encryption';
import Loader from '../../component/loader';

const MENU = [
  { key: 1, link: './', icon: <MdDashboard />, name: 'Dashboard' },
  { key: 2, link: 'order', icon: <MdShoppingCart />, name: 'Orders' },
  { key: 8, link: 'order/returnOrderList', icon: <TbTruckReturn />, name: 'Return Orders' },
  { key: 3, link: 'product', icon: <MdInventory2 />, name: 'Products' },
  { key: 4, link: '/address-shiprocket', icon: <MdLocalShipping />, name: 'Address shipping' },
  { key: 5, link: 'profile', icon: <MdPerson />, name: 'Profile' },
  { key: 6, link: 'changePass', icon: <MdLock />, name: 'Change password' },
  { key: 7, link: 'writeup', icon: <MdInventory2 />, name: 'Seller Guide' }
];

// ── All styles as JS objects ───────────────────────────────────────────────
const S = {
  layout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
    background: '#f5f5f2',
    fontFamily: "'Inter', system-ui, sans-serif"
  },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 56,
    padding: '0 20px',
    background: '#ffffff',
    borderBottom: '1px solid #e8e8e4',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10 },
  logo: { fontSize: 18, fontWeight: 600, letterSpacing: '-0.3px', color: '#1a1a18' },
  logoAccent: { color: '#D85A30' },
  hamburger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#444',
    padding: 4,
    borderRadius: 6
  },

  // Avatar
  avatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: '#e8f1fb',
    color: '#185fa5',
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: '1px solid #b5d4f4',
    userSelect: 'none'
  },

  // Sidebar
  sidebar: {
    flexShrink: 0,
    background: '#ffffff',
    borderRight: '1px solid #e8e8e4',
    padding: '16px 12px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto'
  },

  // Seller card
  sellerCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    background: '#f5f5f2',
    borderRadius: 8,
    border: '1px solid #e8e8e4',
    marginBottom: 18
  },
  sellerAvatar: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    background: '#e8f1fb',
    color: '#185fa5',
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  sellerWelcome: { fontSize: 11, color: '#888', marginBottom: 2 },
  sellerName: { fontSize: 13, fontWeight: 500, color: '#1a1a18', marginBottom: 4 },
  badgeVerified: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 10,
    fontWeight: 500,
    padding: '2px 7px',
    borderRadius: 99,
    background: '#eaf3de',
    color: '#3b6d11'
  },
  badgePending: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 10,
    fontWeight: 500,
    padding: '2px 7px',
    borderRadius: 99,
    background: '#faeeda',
    color: '#854f0b'
  },

  // Nav
  navList: {
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: 0,
    margin: 0
  },
  navIcon: { fontSize: 16, display: 'flex', flexShrink: 0 },
  navDivider: { border: 'none', borderTop: '1px solid #e8e8e4', margin: '10px 0' },
  navLogout: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 12px',
    fontSize: 13,
    color: '#a32d2d',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    borderRadius: 8,
    fontFamily: 'inherit'
  },

  // Mobile overlay + drawer
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    zIndex: 200,
    display: 'flex'
  },
  drawer: {
    width: 240,
    background: '#fff',
    height: '100%',
    padding: '16px 12px',
    overflowY: 'auto'
  },
  drawerClose: {
    display: 'flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#555',
    marginBottom: 12,
    padding: 4,
    borderRadius: 6
  },

  // Main + Footer
  main: { flex: 1, padding: 24, overflowY: 'auto', background: '#f5f5f2' },
  footer: {
    padding: '12px 20px',
    fontSize: 12,
    color: '#888',
    textAlign: 'center',
    borderTop: '1px solid #e8e8e4',
    background: '#fff'
  }
};

// Nav link style helper (active / hover state)
const navLinkStyle = (active, hovered) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 12px',
  fontSize: 13,
  textDecoration: 'none',
  borderRadius: 8,
  width: '100%',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  color: active ? '#185fa5' : hovered ? '#1a1a18' : '#555',
  background: active ? '#e8f1fb' : hovered ? '#f5f5f2' : 'transparent',
  fontWeight: active ? 500 : 400,
  transition: 'background 0.12s, color 0.12s'
});

// Tiny <style> block only for CSS-hover-driven dropdown (no JS equivalent)
const dropdownCSS = `
  .sp-aw { position: relative; padding-bottom: 12px; margin-bottom: -12px; }
  .sp-dd {
    position: absolute; right: 0; top: 100%;
    background: #fff; border: 1px solid #e8e8e4; border-radius: 8px;
    width: 148px; overflow: hidden;
    opacity: 0; pointer-events: none; transform: translateY(-6px);
    transition: opacity .15s, transform .15s;
  }
  .sp-aw:hover .sp-dd { opacity: 1; pointer-events: all; transform: translateY(0); }
  .sp-di {
    display: flex; align-items: center; gap: 8px; padding: 9px 14px;
    font-size: 13px; color: #333; text-decoration: none;
    background: none; border: none; width: 100%; cursor: pointer; font-family: inherit;
  }
  .sp-di:hover { background: #f5f5f2; }
  .sp-di.danger { color: #a32d2d; }
  .sp-di.danger:hover { background: #fcebeb; }
  @media (max-width: 640px) {
    .sp-sidebar-desktop { display: none !important; }
    .sp-hamburger-btn   { display: flex !important; }
  }
`;

// ── Component ──────────────────────────────────────────────────────────────
const Templete = () => {
  const profileStatus = useContext(RegistrationStatus);
  const ForName = useContext(ProfileNameContext);
  const Auth = useContext(AuthUser);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('returnOrderList') || path.includes('returnSingle') || path.includes('refundOrderList') || path.includes('refundSingle')) {
      setActiveKey(8);
    } else if (path.includes('/order')) {
      setActiveKey(2);
    } else if (path.includes('/product')) {
      setActiveKey(3);
    } else if (path.includes('/address-shiprocket')) {
      setActiveKey(4);
    } else if (path.includes('/profile')) {
      setActiveKey(5);
    } else if (path.includes('/changePass')) {
      setActiveKey(6);
    } else if (path.includes('/writeup')) {
      setActiveKey(7);
    } else if (path === '/' || path === '') {
      setActiveKey(1);
    }
  }, [location.pathname]);

  const [nameLoader, setNameLoader] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeKey, setActiveKey] = useState(1);
  const [hoverKey, setHoverKey] = useState(null);

  const appLoad = async () => {
    try {
      setNameLoader(true);
      const token = decrpt(Cookies.get('s_l'));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const { data } = await axios.get('/seller/dashboard');
      if (data.success) {
        profileStatus.setProfile(data.registration_status);
        ForName.setProfileNameState(data.seller_name);
      }
    } catch {
      Auth.setAuth(false);
      Cookies.remove('s_l');
      navigate('/login');
    } finally {
      setNameLoader(false);
    }
  };

  useEffect(() => {
    appLoad();
  }, []);

  const logOut = () => {
    Auth.setAuth(false);
    Cookies.remove('s_l');
    navigate('/login');
  };

  const isVerified = profileStatus?.profile == 6;
  const sellerName = ForName?.profileNameState ?? '';
  const initials = sellerName
    ? sellerName
      .split(' ')
      .map((w) => w[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
    : '—';

  const SellerCard = ({ collapsed }) => (
    <div style={{ ...S.sellerCard, justifyContent: collapsed ? 'center' : 'flex-start', padding: collapsed ? '12px 4px' : 12, marginBottom: collapsed ? 10 : 18 }}>
      <div style={{ ...S.sellerAvatar, width: collapsed ? 32 : 38, height: collapsed ? 32 : 38, fontSize: collapsed ? 12 : 13 }}>{initials}</div>
      {!collapsed && (
        <div>
          <p style={S.sellerWelcome}>Welcome back</p>
          {nameLoader ? <Loader /> : <p style={S.sellerName}>{sellerName || 'Seller'}</p>}
          <span style={isVerified ? S.badgeVerified : S.badgePending}>
            {isVerified ? (
              <>
                <MdVerified size={11} /> Verified
              </>
            ) : (
              <>
                <MdPending size={11} /> Pending
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );

  const NavLinks = ({ onLinkClick, collapsed }) => (
    <ul style={S.navList}>
      {MENU.map(({ key, link, icon, name }) => (
        <li key={key} title={collapsed ? name : undefined}>
          <Link
            to={link}
            style={{ ...navLinkStyle(activeKey === key, hoverKey === key), padding: collapsed ? '12px' : '9px 12px', justifyContent: collapsed ? 'center' : 'flex-start' }}
            onMouseEnter={() => setHoverKey(key)}
            onMouseLeave={() => setHoverKey(null)}
            onClick={() => {
              setActiveKey(key);
              onLinkClick?.();
            }}>
            <span style={{ ...S.navIcon, fontSize: collapsed ? 20 : 16 }} aria-hidden="true">
              {icon}
            </span>
            {!collapsed && name}
          </Link>
        </li>
      ))}
      <hr style={S.navDivider} />
      <li title={collapsed ? "Log out" : undefined}>
        <button style={{ ...S.navLogout, padding: collapsed ? '12px' : '9px 12px', justifyContent: collapsed ? 'center' : 'flex-start' }} onClick={logOut}>
          <span style={{ ...S.navIcon, fontSize: collapsed ? 20 : 16 }} aria-hidden="true">
            <MdLogout />
          </span>
          {!collapsed && "Log out"}
        </button>
      </li>
    </ul>
  );

  return (
    <div style={S.layout}>
      <style>{dropdownCSS}</style>

      {/* ── Header ── */}
      <header style={S.header}>
        <div style={S.headerLeft}>
          {/* Hidden on desktop via CSS class */}
          <button
            className="sp-hamburger-btn"
            style={{ ...S.hamburger, display: 'none' }}
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu">
            <MdMenu size={22} />
          </button>
          {/* Hidden on mobile via CSS class */}
          <button
            className="sp-sidebar-desktop"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', marginRight: 8, display: 'flex', alignItems: 'center' }}
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label="Toggle sidebar">
            <MdMenu size={22} />
          </button>
          <span style={S.logo}>
            Aivi<span style={S.logoAccent}>Mart</span>
          </span>
        </div>

        <div style={S.headerRight}>
          <div className="sp-aw">
            <div style={S.avatar}>{initials}</div>
            <div className="sp-dd">
              <Link to="profile" className="sp-di">
                <MdPerson size={15} /> Profile
              </Link>
              <button className="sp-di danger" onClick={logOut}>
                <MdLogout size={15} /> Log out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div style={S.body}>
        {/* ── Desktop Sidebar ── */}
        <aside className="sp-sidebar-desktop" style={{ ...S.sidebar, width: isCollapsed ? 64 : 224, padding: isCollapsed ? '16px 8px' : '16px 12px', transition: 'width 0.2s ease-in-out' }}>
          <SellerCard collapsed={isCollapsed} />
          <NavLinks collapsed={isCollapsed} />
        </aside>

        {/* ── Mobile Drawer ── */}
        {mobileOpen && (
          <div style={S.overlay} onClick={() => setMobileOpen(false)}>
            <aside style={S.drawer} onClick={(e) => e.stopPropagation()}>
              <button
                style={S.drawerClose}
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu">
                <MdClose size={20} />
              </button>
              <SellerCard collapsed={false} />
              <NavLinks onLinkClick={() => setMobileOpen(false)} collapsed={false} />
            </aside>
          </div>
        )}

        {/* ── Main ── */}
        <main style={S.main}>
          <Outlet />
        </main>
      </div>

      <footer style={S.footer}>©2024 AiviMart. All rights reserved.</footer>
    </div>
  );
};

export default Templete;
